import { Alert, App, Button, Modal } from 'antd';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CurrentUser } from '../../../api/active/generated/types';
import { requestPendingSaveLeaveCheck } from '../../../api/active/roleViewEditorGuard';
import { isOfflineDemoSessionEnabled } from '../../../api/active/sessionProvider';
import {
  type FormalWorkspaceRecoveryPort,
  WorkspaceRecoveryConflictError,
} from '../../../quote-integration/formalWorkspaceRecoveryStore';
import {
  type FormalWorkspaceSessionScope,
  formalWorkspaceSessionInputs,
  formalWorkspaceSessionStorageKey,
  hydrateFormalWorkspaceSession,
  hydrateFormalWorkspaceSnapshot,
  parseFormalWorkspaceSnapshot,
  readFormalWorkspaceSession,
  sameFormalWorkspaceSessionInputs,
  serializeFormalWorkspaceSession,
  serializeFormalWorkspaceTabs,
} from '../../../quote-integration/formalWorkspaceSession';
import {
  formalQuoteTabs,
  isFormalNewQuoteId,
} from '../../../quote-integration/quoteLegacyProjection';
import {
  bindKnownQuoteVersion,
  quoteWorkspaceSearch,
  readQuoteWorkspaceRoute,
} from '../../../quote-integration/quoteWorkspaceRoute';
import { hasUnsavedFormalWorkspaceDrafts } from '../../../quote-integration/roleViewDraftGuard';
import {
  type AcceptanceIdentity,
  type AcceptanceIdentityPort,
  acceptanceIdentityFromState,
  applyAcceptanceIdentity,
  useAcceptanceIdentitySelector,
} from './acceptanceIdentity';
import { isCasePreviewEnabled, isCaseView } from './caseFeature';
import {
  caseDraftSnapshots,
  projectCasePreview,
} from './casePreviewProjection';
import {
  deviceAssetIdsInState,
  useOptionalDeviceAssetStore,
} from './deviceAssetStore';
import {
  hasValidStockReminderBinding,
  inventoryReminderAccess,
} from './inventoryReminderState';
import { canUseActivePermissionAction } from './permissionPolicy';
import { pendingReceiptQuoteCount } from './receiptState';
import type { CpqLoadResult, CpqRepositoryPort } from './repository';
import {
  type StorageFailureDiagnostic,
  storageFailureDiagnostic,
  storageFailureSummary,
} from './storageFailure';
import type { AppView, CpqState, StorageLike } from './types';
import type { UnifiedFieldValuePort } from './unifiedFieldPort';
import {
  canAccessSettings,
  canUsePermissionCenter,
  guardRoleView,
} from './viewGuard';
import type { WorkspacePreferencesPort } from './workspacePreferences';

type CpqRoute = Pick<
  CpqState,
  | 'activeView'
  | 'activeQuoteId'
  | 'activeQuoteVersionId'
  | 'workspacePicker'
  | 'openQuoteIds'
>;
type QuoteLeaveGuard = () => Promise<void>;

export type FormalWorkspaceRecoveryCheckpoint = {
  scopeKey: string | null;
  requestedSnapshot: string | null;
  requestRevision: number;
};

interface CpqContextValue {
  state: CpqState;
  setState: React.Dispatch<React.SetStateAction<CpqState>>;
  commit: (updater: (current: CpqState) => CpqState) => void;
  hasPersistedCaseRehearsalQuote: (quoteId: string) => Promise<boolean>;
  mutateTransient: (updater: (current: CpqState) => CpqState) => void;
  getCurrentState: () => CpqState;
  ownsWorkspaceSession: (authorizationKey: string | undefined) => boolean;
  registerQuoteLeaveGuard: (guard: QuoteLeaveGuard) => () => void;
  navigateRoute: (
    updater: (
      current: CpqState,
    ) => Pick<
      CpqState,
      | 'activeView'
      | 'activeQuoteId'
      | 'activeQuoteVersionId'
      | 'workspacePicker'
      | 'openQuoteIds'
    >,
  ) => Promise<boolean>;
  reset: () => void;
  clearCustomerBindingHistory: () => void;
  resetEpoch: number;
  workspaceSessionScope?: FormalWorkspaceSessionScope;
  ensureFormalWorkspaceRecovery: (
    verify?: boolean,
  ) => Promise<FormalWorkspaceRecoveryCheckpoint | null>;
  isFormalWorkspaceRecoveryCurrent: (
    checkpoint: FormalWorkspaceRecoveryCheckpoint,
  ) => boolean;
  workspacePreferencesPort: WorkspacePreferencesPort;
  fieldValuePort: UnifiedFieldValuePort;
  bindAcceptanceIdentity: (identity: AcceptanceIdentity) => void;
}

const CpqContext = createContext<CpqContextValue | null>(null);

const UNCONFIGURED_FIELD_VALUE_PORT: UnifiedFieldValuePort = {
  read: () => {
    throw new Error('企业字段值端口未注入');
  },
  readAll: () => {
    throw new Error('企业字段值端口未注入');
  },
  write: () => {
    throw new Error('企业字段值端口未注入');
  },
  replace: () => {
    throw new Error('企业字段值端口未注入');
  },
};

/** Formal pages use the current server session gate in AppShell; fixture roles never decide their route. */
function guardFormalOrDisposableView(
  state: CpqState,
  formal: boolean,
): CpqState {
  if (!formal) return guardRoleView(state);
  const activeView =
    state.activeView === 'options'
      ? 'plans'
      : isCaseView(state.activeView) && !isCasePreviewEnabled()
        ? 'workspace'
        : state.activeView;
  return activeView === state.activeView ? state : { ...state, activeView };
}

function activeViewForLocation(
  state: CpqState,
  transientRoutes = false,
): CpqState {
  const guardCurrentView = (next: CpqState) =>
    guardFormalOrDisposableView(next, transientRoutes);
  if (typeof window === 'undefined') return state;
  const hash = window.location.hash;
  if (hash === '#share' || hash.startsWith('#share/')) {
    return guardCurrentView({ ...state, activeView: 'share' });
  }
  const search = new URLSearchParams(window.location.search);
  const hasExplicitQuoteRoute =
    search.has('quoteId') || search.has('quoteVersionId');
  if (transientRoutes) {
    const selected = readQuoteWorkspaceRoute(window.location.search);
    if (selected)
      return guardCurrentView({
        ...state,
        activeView: 'workspace',
        activeQuoteId: selected.quoteId,
        activeQuoteVersionId: bindKnownQuoteVersion(
          selected.quoteId,
          selected.quoteVersionId,
          state.quotes,
        ),
        openQuoteIds: formalQuoteTabs(state, selected.quoteId),
      });
  }
  const offlineDemoFlag = (
    import.meta.env as ImportMetaEnv & {
      readonly VITE_CPQ_OFFLINE_DEMO?: string;
    }
  ).VITE_CPQ_OFFLINE_DEMO;
  if (
    !hasExplicitQuoteRoute &&
    isOfflineDemoSessionEnabled({
      dev: import.meta.env.DEV,
      flag: offlineDemoFlag,
      hostname: window.location.hostname,
    })
  ) {
    return guardCurrentView({ ...state, activeView: 'cases' });
  }
  return guardCurrentView({ ...state, activeView: 'workspace' });
}

export function CpqProvider({
  children,
  repositoryPort,
  preferencesPort,
  fieldValuePort,
  acceptanceIdentityPort,
  workspaceSessionStorage,
  workspaceRecoveryPort,
  initialWorkspaceRecovery,
  workspaceSessionScope,
  transientRoutes = false,
}: {
  children: ReactNode;
  repositoryPort: CpqRepositoryPort;
  preferencesPort: WorkspacePreferencesPort;
  fieldValuePort?: UnifiedFieldValuePort;
  acceptanceIdentityPort?: AcceptanceIdentityPort;
  workspaceSessionStorage?: StorageLike;
  workspaceRecoveryPort?: FormalWorkspaceRecoveryPort;
  initialWorkspaceRecovery?: string | null;
  workspaceSessionScope?: FormalWorkspaceSessionScope;
  transientRoutes?: boolean;
}) {
  const { message } = App.useApp();
  const guardCurrentView = useCallback(
    (next: CpqState) => guardFormalOrDisposableView(next, transientRoutes),
    [transientRoutes],
  );
  const workspaceAuthorizationRef = useRef(
    workspaceSessionScope?.authorizationKey,
  );
  workspaceAuthorizationRef.current = workspaceSessionScope?.authorizationKey;
  const providerAlive = useRef(true);
  useEffect(() => {
    providerAlive.current = true;
    return () => {
      providerAlive.current = false;
    };
  }, []);
  const repository = repositoryPort;
  const workspacePreferencesPort = preferencesPort;
  const unifiedFieldValuePort = fieldValuePort ?? UNCONFIGURED_FIELD_VALUE_PORT;
  const deviceAssetStore = useOptionalDeviceAssetStore();
  const [initialLoad] = useState<CpqLoadResult>(() => repository.load());
  const fallbackIdentityPort = useRef<AcceptanceIdentityPort | null>(null);
  if (!fallbackIdentityPort.current) {
    let identity: AcceptanceIdentity | null = acceptanceIdentityFromState(
      initialLoad.state,
    );
    fallbackIdentityPort.current = {
      load: () => identity,
      save: (next) => {
        identity = next;
      },
      clear: () => {
        identity = null;
      },
    };
  }
  const identityPort = acceptanceIdentityPort ?? fallbackIdentityPort.current;
  const [initialRoute] = useState(() => {
    const placeholderIds = new Set<string>();
    const identityState = applyAcceptanceIdentity(
      initialLoad.state,
      identityPort.load() ?? acceptanceIdentityFromState(initialLoad.state),
    );
    const initialState =
      transientRoutes && workspaceSessionScope
        ? workspaceRecoveryPort
          ? hydrateFormalWorkspaceSnapshot(
              identityState,
              initialWorkspaceRecovery ?? null,
              workspaceSessionScope,
            )
          : workspaceSessionStorage
            ? hydrateFormalWorkspaceSession(
                identityState,
                workspaceSessionStorage,
                workspaceSessionScope,
              )
            : identityState
        : identityState;
    try {
      return {
        state: guardCurrentView(
          projectCasePreview(
            activeViewForLocation(initialState, transientRoutes),
            identityState,
            caseDraftSnapshots(initialState),
            placeholderIds,
          ),
        ),
        hiddenCases: caseDraftSnapshots(initialState),
        placeholderIds,
        error: null,
      };
    } catch {
      return {
        state: projectCasePreview(
          initialState,
          identityState,
          {},
          placeholderIds,
        ),
        hiddenCases: caseDraftSnapshots(initialState),
        placeholderIds,
        error: '报价工作台地址参数无效，请从报价管理重新打开。',
      };
    }
  });
  const [state, updateState] = useState<CpqState>(initialRoute.state);
  const [routeError, setRouteError] = useState(initialRoute.error);
  useEffect(() => {
    if (
      routeError ||
      !transientRoutes ||
      window.location.hash.startsWith('#share')
    )
      return;
    const quoteId =
      state.activeView === 'workspace' &&
      /^[1-9][0-9]{0,18}$/.test(state.activeQuoteId)
        ? state.activeQuoteId
        : undefined;
    const search = quoteWorkspaceSearch(
      window.location.search,
      quoteId,
      state.activeQuoteVersionId,
    );
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${search}${window.location.hash}`,
    );
  }, [
    routeError,
    transientRoutes,
    state.activeView,
    state.activeQuoteId,
    state.activeQuoteVersionId,
  ]);
  const currentState = useRef(state);
  currentState.current = state;
  const persistenceContract = useRef(initialLoad);
  // The last accepted runtime also includes tab-local formal drafts that v5
  // deliberately excludes. A failed case write must roll back to this state.
  const persistenceRuntime = useRef(initialRoute.state);
  const setState = useCallback<React.Dispatch<React.SetStateAction<CpqState>>>(
    (update) => {
      const current = currentState.current;
      const next = typeof update === 'function' ? update(current) : update;
      const projected = guardCurrentView(
        projectCasePreview(
          next,
          current,
          {
            ...initialRoute.hiddenCases,
            ...caseDraftSnapshots(persistenceContract.current.state),
          },
          initialRoute.placeholderIds,
        ),
      );
      currentState.current = projected;
      updateState(projected);
    },
    [guardCurrentView, initialRoute.hiddenCases, initialRoute.placeholderIds],
  );
  const skipNextPersist = useRef(false);
  const quoteLeaveGuard = useRef<QuoteLeaveGuard | null>(null);
  const navigationPending = useRef(false);
  const getCurrentState = useCallback(() => currentState.current, []);
  const registerQuoteLeaveGuard = useCallback((guard: QuoteLeaveGuard) => {
    quoteLeaveGuard.current = guard;
    return () => {
      if (quoteLeaveGuard.current === guard) quoteLeaveGuard.current = null;
    };
  }, []);
  const navigateRoute = useCallback(
    async (updater: (current: CpqState) => CpqRoute): Promise<boolean> => {
      if (navigationPending.current) return false;
      const initial = currentState.current;
      const target = updater(initial);
      const leaving =
        initial.activeView === 'workspace' &&
        (target.activeView !== 'workspace' ||
          target.activeQuoteId !== initial.activeQuoteId ||
          (target.activeQuoteVersionId !== undefined &&
            target.activeQuoteVersionId !== initial.activeQuoteVersionId));
      navigationPending.current = true;
      try {
        if (target.activeView !== initial.activeView)
          requestPendingSaveLeaveCheck();
        if (transientRoutes && leaving && quoteLeaveGuard.current)
          await quoteLeaveGuard.current();
        const current = currentState.current;
        const route = updater(current);
        const routed = {
          ...current,
          ...(route.activeQuoteId !== current.activeQuoteId
            ? { activeQuoteVersionId: undefined }
            : {}),
          ...route,
        };
        const next = guardCurrentView({
          ...routed,
          activeQuoteVersionId: bindKnownQuoteVersion(
            routed.activeQuoteId,
            routed.activeQuoteVersionId,
            routed.quotes,
          ),
        });
        if (transientRoutes) skipNextPersist.current = true;
        currentState.current = next;
        setState(next);
        return true;
      } catch (error) {
        message.error(
          error instanceof Error ? error.message : '保存失败，未离开报价',
        );
        return false;
      } finally {
        navigationPending.current = false;
      }
    },
    [guardCurrentView, message, transientRoutes],
  );
  useEffect(() => {
    if (!transientRoutes) return;
    const warnUnsaved = (event: BeforeUnloadEvent) => {
      if (
        !Object.values(currentState.current.quotes).some((quote) => quote.dirty)
      )
        return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnUnsaved);
    return () => window.removeEventListener('beforeunload', warnUnsaved);
  }, [transientRoutes]);
  const [resetEpoch, setResetEpoch] = useState(0);
  const [workspaceRecoveryFailure, setWorkspaceRecoveryFailure] =
    useState<StorageFailureDiagnostic | null>(null);
  const [recoveryConflictOpen, setRecoveryConflictOpen] = useState(false);
  const [takingOverRecovery, setTakingOverRecovery] = useState(false);
  // A stale compare-and-swap token cannot become current by retrying writes.
  // Keep later edits in memory and show a nonblocking warning instead of retrying.
  const conflictedRecoveryScope = useRef<string | null>(null);
  const recoveryAlive = useRef(true);
  const durableRecoveryPayload = useRef<string | null>(null);
  const recoveryInputs = useRef<ReturnType<
    typeof formalWorkspaceSessionInputs
  > | null>(null);
  const durableRecoveryInputs = useRef<ReturnType<
    typeof formalWorkspaceSessionInputs
  > | null>(null);
  const recoveryTail = useRef<{
    requestedSnapshot: string;
    operation: Promise<FormalWorkspaceRecoveryCheckpoint | null>;
  } | null>(null);
  const latestRecoveryRequest = useRef(0);
  useEffect(() => {
    recoveryAlive.current = true;
    return () => {
      recoveryAlive.current = false;
    };
  }, []);
  const isFormalWorkspaceRecoveryCurrent = useCallback(
    (checkpoint: FormalWorkspaceRecoveryCheckpoint) => {
      if (!recoveryAlive.current) return false;
      if (!transientRoutes || !workspaceSessionScope)
        return (
          checkpoint.scopeKey === null && checkpoint.requestedSnapshot === null
        );
      try {
        return (
          checkpoint.scopeKey ===
            formalWorkspaceSessionStorageKey(workspaceSessionScope) &&
          checkpoint.requestRevision === latestRecoveryRequest.current &&
          durableRecoveryPayload.current === checkpoint.requestedSnapshot &&
          checkpoint.requestedSnapshot ===
            serializeFormalWorkspaceSession(
              currentState.current,
              workspaceSessionScope,
            )
        );
      } catch {
        return false;
      }
    },
    [transientRoutes, workspaceSessionScope],
  );
  const mergeRecoverySnapshot = useCallback(
    (requestedSnapshot: string, previous: string | null) => {
      if (isCasePreviewEnabled() || !previous) return requestedSnapshot;
      if (!workspaceSessionScope) return requestedSnapshot;
      const previousTabs = parseFormalWorkspaceSnapshot(
        previous,
        workspaceSessionScope,
      );
      if (!previousTabs)
        throw new Error('Invalid existing workspace recovery snapshot');
      const next = parseFormalWorkspaceSnapshot(
        requestedSnapshot,
        workspaceSessionScope,
      );
      if (!next) throw new Error('Invalid workspace recovery snapshot');
      const hidden = caseDraftSnapshots({
        quotes: previousTabs.quotes,
      } as CpqState);
      if (Object.keys(hidden).some((id) => next.quotes[id]))
        throw new Error('CASE_PREVIEW_RECOVERY_ID_CONFLICT');
      return serializeFormalWorkspaceTabs(
        { ...next, quotes: { ...next.quotes, ...hidden } },
        workspaceSessionScope,
      );
    },
    [workspaceSessionScope],
  );
  const ensureFormalWorkspaceRecovery = useCallback(
    async (
      verify = false,
      manualRetry = false,
    ): Promise<FormalWorkspaceRecoveryCheckpoint | null> => {
      if (
        !transientRoutes ||
        !workspaceSessionScope ||
        (!workspaceRecoveryPort && !workspaceSessionStorage)
      )
        return { scopeKey: null, requestedSnapshot: null, requestRevision: 0 };
      const key = formalWorkspaceSessionStorageKey(workspaceSessionScope);
      if (conflictedRecoveryScope.current === key && !manualRetry) return null;
      const inputs = formalWorkspaceSessionInputs(
        currentState.current,
        workspaceSessionScope,
      );
      if (
        !verify &&
        !manualRetry &&
        sameFormalWorkspaceSessionInputs(recoveryInputs.current, inputs)
      ) {
        if (recoveryTail.current) return recoveryTail.current.operation;
        if (
          durableRecoveryPayload.current &&
          sameFormalWorkspaceSessionInputs(
            durableRecoveryInputs.current,
            inputs,
          )
        )
          return {
            scopeKey: key,
            requestedSnapshot: durableRecoveryPayload.current,
            requestRevision: latestRecoveryRequest.current,
          };
      }
      let requestedSnapshot: string;
      try {
        requestedSnapshot = serializeFormalWorkspaceSession(
          currentState.current,
          workspaceSessionScope,
        );
      } catch (cause) {
        setWorkspaceRecoveryFailure(
          storageFailureDiagnostic(cause, 'serialize', key),
        );
        return null;
      }
      recoveryInputs.current = inputs;
      // Only the queue tail may dedupe. Reusing an older A after B was queued
      // would let B become durable after the current editor had returned to A.
      if (
        !verify &&
        !manualRetry &&
        recoveryTail.current?.requestedSnapshot === requestedSnapshot
      )
        return recoveryTail.current.operation;
      if (
        !verify &&
        !recoveryTail.current &&
        durableRecoveryPayload.current === requestedSnapshot
      )
        return {
          scopeKey: key,
          requestedSnapshot,
          requestRevision: latestRecoveryRequest.current,
        };
      const request = ++latestRecoveryRequest.current;
      const checkpoint = {
        scopeKey: key,
        requestedSnapshot,
        requestRevision: request,
      };
      const mergePrevious = (previous: string | null) =>
        mergeRecoverySnapshot(requestedSnapshot, previous);
      const operation = (async () => {
        try {
          if (workspaceRecoveryPort)
            await workspaceRecoveryPort.save(
              workspaceSessionScope,
              requestedSnapshot,
              mergePrevious,
            );
          else if (workspaceSessionStorage) {
            // Explicit compatibility port for existing disposable/synchronous consumers.
            // The production root injects only the asynchronous durable port.
            const previous = readFormalWorkspaceSession(
              workspaceSessionStorage,
              workspaceSessionScope,
            );
            workspaceSessionStorage.setItem(
              key,
              mergePrevious(
                previous
                  ? serializeFormalWorkspaceTabs(
                      previous,
                      workspaceSessionScope,
                    )
                  : null,
              ),
            );
          }
          if (recoveryAlive.current) {
            if (conflictedRecoveryScope.current === key)
              conflictedRecoveryScope.current = null;
            durableRecoveryPayload.current = requestedSnapshot;
            durableRecoveryInputs.current = inputs;
            if (request === latestRecoveryRequest.current)
              setWorkspaceRecoveryFailure(null);
          }
          return checkpoint;
        } catch (cause) {
          if (cause instanceof WorkspaceRecoveryConflictError)
            conflictedRecoveryScope.current = key;
          if (
            recoveryAlive.current &&
            request === latestRecoveryRequest.current
          )
            setWorkspaceRecoveryFailure(
              storageFailureDiagnostic(
                cause,
                workspaceRecoveryPort ? 'transaction' : 'setItem',
                key,
                requestedSnapshot,
              ),
            );
          return null;
        }
      })();
      recoveryTail.current = { requestedSnapshot, operation };
      void operation.then(() => {
        if (recoveryTail.current?.operation === operation)
          recoveryTail.current = null;
      });
      return operation;
    },
    [
      transientRoutes,
      workspaceSessionStorage,
      workspaceRecoveryPort,
      workspaceSessionScope,
      mergeRecoverySnapshot,
    ],
  );
  const takeOverFormalWorkspaceRecovery = useCallback(async () => {
    const cause = workspaceRecoveryFailure?.cause;
    if (
      !(cause instanceof WorkspaceRecoveryConflictError) ||
      !workspaceRecoveryPort?.takeOver ||
      !workspaceSessionScope ||
      takingOverRecovery
    )
      return;
    const key = formalWorkspaceSessionStorageKey(workspaceSessionScope);
    let requestedSnapshot: string;
    try {
      requestedSnapshot = serializeFormalWorkspaceSession(
        currentState.current,
        workspaceSessionScope,
      );
    } catch (error) {
      setWorkspaceRecoveryFailure(
        storageFailureDiagnostic(error, 'serialize', key),
      );
      return;
    }
    setTakingOverRecovery(true);
    const request = ++latestRecoveryRequest.current;
    try {
      const inputs = formalWorkspaceSessionInputs(
        currentState.current,
        workspaceSessionScope,
      );
      await workspaceRecoveryPort.takeOver(
        workspaceSessionScope,
        requestedSnapshot,
        cause.conflictingSnapshot,
        (previous) => mergeRecoverySnapshot(requestedSnapshot, previous),
      );
      conflictedRecoveryScope.current = null;
      durableRecoveryPayload.current = requestedSnapshot;
      recoveryInputs.current = inputs;
      durableRecoveryInputs.current = inputs;
      setWorkspaceRecoveryFailure(null);
      setRecoveryConflictOpen(false);
      if (request === latestRecoveryRequest.current) {
        try {
          if (
            serializeFormalWorkspaceSession(
              currentState.current,
              workspaceSessionScope,
            ) !== requestedSnapshot
          )
            void ensureFormalWorkspaceRecovery();
        } catch (error) {
          setWorkspaceRecoveryFailure(
            storageFailureDiagnostic(error, 'serialize', key),
          );
        }
      }
    } catch (error) {
      if (error instanceof WorkspaceRecoveryConflictError)
        conflictedRecoveryScope.current = key;
      setWorkspaceRecoveryFailure(
        storageFailureDiagnostic(error, 'transaction', key, requestedSnapshot),
      );
    } finally {
      setTakingOverRecovery(false);
    }
  }, [
    ensureFormalWorkspaceRecovery,
    mergeRecoverySnapshot,
    takingOverRecovery,
    workspaceRecoveryFailure,
    workspaceRecoveryPort,
    workspaceSessionScope,
  ]);
  useEffect(() => {
    // Only this exact workspace payload is persisted; unrelated catalog/view updates dedupe.
    void state;
    void ensureFormalWorkspaceRecovery();
  }, [state, ensureFormalWorkspaceRecovery]);
  useEffect(() => {
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      persistenceRuntime.current = state;
      return;
    }
    // 精确正式地址可能先于服务端实体加载；不能写进旧 v5 仓储后因不变量失败回滚路由。
    if (
      routeError ||
      (!isCasePreviewEnabled() &&
        initialRoute.placeholderIds.has(state.activeQuoteId)) ||
      (transientRoutes &&
        (isFormalNewQuoteId(state.activeQuoteId) ||
          /^[1-9][0-9]{0,18}$/.test(state.activeQuoteId)))
    ) {
      persistenceRuntime.current = state;
      return;
    }
    const contract = persistenceContract.current;
    const caseQuotes = Object.fromEntries(
      Object.entries(state.quotes).filter(
        ([, quote]) => quote.caseRehearsalSnapshot?.kind === 'case-rehearsal',
      ),
    );
    // 案例草稿使用本地目录快照；正式工作台的临时目录与报价标签继续留在运行态。
    let businessState =
      transientRoutes && caseQuotes[state.activeQuoteId]
        ? {
            ...contract.state,
            quotes: { ...contract.state.quotes, ...caseQuotes },
            nextSequence: Math.max(
              contract.state.nextSequence,
              state.nextSequence,
            ),
            audits: state.audits,
            activeView: state.activeView,
            activeQuoteId: state.activeQuoteId,
            activeQuoteVersionId: undefined,
            openQuoteIds: state.openQuoteIds.filter(
              (id) =>
                Boolean(caseQuotes[id]) || Boolean(contract.state.quotes[id]),
            ),
          }
        : state;
    // 关闭只投影运行态；普通写入仍保留仓储中的演练草稿，不能静默删除。
    if (!isCasePreviewEnabled())
      businessState = {
        ...businessState,
        quotes: {
          ...Object.fromEntries(
            Object.entries(businessState.quotes).filter(
              ([id]) => !initialRoute.placeholderIds.has(id),
            ),
          ),
          ...caseDraftSnapshots(contract.state),
        },
        openQuoteIds: businessState.openQuoteIds.filter(
          (id) => !initialRoute.placeholderIds.has(id),
        ),
      };
    const result = repository.persist(contract, businessState);
    if (result.status === 'storage-write-error') {
      const persistedAssetIds = deviceAssetIdsInState(contract.state);
      const attemptedAssetIds = deviceAssetIdsInState(businessState);
      const stagedAssetIds = [...attemptedAssetIds].filter(
        (assetId) => !persistedAssetIds.has(assetId),
      );
      if (stagedAssetIds.length && deviceAssetStore) {
        void deviceAssetStore.deleteMany(stagedAssetIds).catch(() => {
          message.warning(
            '业务状态已回滚，但临时图片资产清理失败；请刷新后检查浏览器资产存储',
          );
        });
      }
      persistenceContract.current = { ...contract, autoPersist: false };
      const rollback =
        transientRoutes && caseQuotes[state.activeQuoteId]
          ? persistenceRuntime.current
          : contract.state;
      currentState.current = rollback;
      setState(rollback);
      message.error(
        result.error instanceof Error
          ? `本地存储失败，本次业务变更已回滚且未保存：${result.error.message}`
          : '本地存储失败，本次业务变更已回滚且未保存',
      );
      return;
    }
    if (
      result.status === 'persisted-v5' ||
      result.status === 'migration-complete' ||
      result.status === 'legacy-cleanup-error'
    ) {
      persistenceContract.current = {
        state: businessState,
        status: 'valid-v5',
        autoPersist: true,
      };
    }
    persistenceRuntime.current = state;
  }, [
    deviceAssetStore,
    message,
    repository,
    state,
    transientRoutes,
    routeError,
    initialRoute.placeholderIds,
  ]);

  useEffect(() => {
    let active = true;
    let refreshRevision = 0;
    const unsubscribe = repository.subscribe?.(() => {
      const revision = ++refreshRevision;
      void Promise.resolve()
        .then(() => (repository.refresh ?? repository.load)())
        .then((refreshed) => {
          if (!active || revision !== refreshRevision) return;
          if (
            !['missing', 'valid-v5', 'migrated-v4', 'migrated-v3'].includes(
              refreshed.status,
            )
          ) {
            message.error('共享业务数据无效，已拒绝跨标签页更新');
            return;
          }
          try {
            const current = currentState.current;
            const localIdentity =
              identityPort.load() ?? acceptanceIdentityFromState(current);
            // v5 is the shared Disposable repository, not the authority for
            // this tab's formal drafts, route or API-loaded catalog. Import
            // case drafts without replacing local edits or switching tabs.
            const next = transientRoutes
              ? {
                  ...current,
                  nextSequence: Math.max(
                    current.nextSequence,
                    refreshed.state.nextSequence,
                  ),
                  quotes: {
                    ...Object.fromEntries(
                      Object.entries(refreshed.state.quotes).filter(
                        ([, quote]) =>
                          quote.caseRehearsalSnapshot?.kind ===
                          'case-rehearsal',
                      ),
                    ),
                    ...Object.fromEntries(
                      Object.entries(current.quotes).filter(
                        ([id, quote]) =>
                          !quote.caseRehearsalSnapshot ||
                          current.openQuoteIds.includes(id) ||
                          quote.dirty,
                      ),
                    ),
                  },
                }
              : activeViewForLocation(
                  applyAcceptanceIdentity(refreshed.state, localIdentity),
                );
            persistenceContract.current = refreshed;
            skipNextPersist.current = true;
            currentState.current = next;
            setState(next);
          } catch (error) {
            message.error(
              error instanceof Error ? error.message : '验收身份已失效',
            );
          }
        })
        .catch((error: unknown) => {
          if (!active || revision !== refreshRevision) return;
          message.error(
            error instanceof Error
              ? error.message
              : '共享业务数据刷新失败，未应用跨标签页更新',
          );
        });
    });
    return () => {
      active = false;
      refreshRevision += 1;
      unsubscribe?.();
    };
  }, [identityPort, message, repository, transientRoutes]);

  useEffect(() => {
    const applyLocationRoute = () => {
      try {
        const next = activeViewForLocation(
          currentState.current,
          transientRoutes,
        );
        if (transientRoutes) {
          void navigateRoute(() => ({
            activeView: next.activeView,
            activeQuoteId: next.activeQuoteId,
            activeQuoteVersionId: next.activeQuoteVersionId,
            openQuoteIds: next.openQuoteIds,
            workspacePicker: next.workspacePicker,
          })).then((accepted) => {
            if (accepted) {
              setRouteError(null);
              return;
            }
            const current = currentState.current;
            const search = quoteWorkspaceSearch(
              window.location.search,
              /^[1-9][0-9]{0,18}$/.test(current.activeQuoteId)
                ? current.activeQuoteId
                : undefined,
              current.activeQuoteVersionId,
            );
            window.history.replaceState(
              window.history.state,
              '',
              `${window.location.pathname}${search}`,
            );
          });
        } else {
          setRouteError(null);
          setState(next);
        }
      } catch {
        if (
          transientRoutes &&
          currentState.current.quotes[currentState.current.activeQuoteId]?.dirty
        ) {
          message.error('报价地址参数无效，已保留当前未保存编辑');
          const current = currentState.current;
          window.history.replaceState(
            window.history.state,
            '',
            `${window.location.pathname}${quoteWorkspaceSearch('', /^[1-9][0-9]{0,18}$/.test(current.activeQuoteId) ? current.activeQuoteId : undefined, current.activeQuoteVersionId)}`,
          );
        } else setRouteError('报价工作台地址参数无效，请从报价管理重新打开。');
      }
    };
    applyLocationRoute();
    window.addEventListener('hashchange', applyLocationRoute);
    window.addEventListener('popstate', applyLocationRoute);
    return () => {
      window.removeEventListener('hashchange', applyLocationRoute);
      window.removeEventListener('popstate', applyLocationRoute);
    };
  }, [transientRoutes, navigateRoute, message]);

  const value = useMemo<CpqContextValue>(
    () => ({
      state,
      getCurrentState,
      ownsWorkspaceSession: (authorizationKey) =>
        providerAlive.current &&
        workspaceAuthorizationRef.current === authorizationKey,
      registerQuoteLeaveGuard,
      resetEpoch,
      workspaceSessionScope,
      ensureFormalWorkspaceRecovery,
      isFormalWorkspaceRecoveryCurrent,
      workspacePreferencesPort,
      fieldValuePort: unifiedFieldValuePort,
      bindAcceptanceIdentity(identity) {
        try {
          const next = applyAcceptanceIdentity(state, identity);
          identityPort.save(acceptanceIdentityFromState(next));
          setState(guardCurrentView(next));
        } catch (error) {
          message.error(
            error instanceof Error ? error.message : '验收身份绑定失败',
          );
        }
      },
      setState,
      commit(updater) {
        try {
          setState((current) => guardCurrentView(updater(current)));
        } catch (error) {
          message.error(error instanceof Error ? error.message : '操作失败');
        }
      },
      async hasPersistedCaseRehearsalQuote(quoteId) {
        if (!isCasePreviewEnabled()) return false;
        const loaded = await (repository.refresh ?? repository.load)();
        return (
          loaded.status === 'valid-v5' &&
          loaded.state.quotes[quoteId]?.caseRehearsalSnapshot?.kind ===
            'case-rehearsal'
        );
      },
      mutateTransient(updater) {
        try {
          skipNextPersist.current = true;
          const next = guardCurrentView(updater(currentState.current));
          if (transientRoutes) {
            for (const [quoteId, quote] of Object.entries(
              currentState.current.quotes,
            )) {
              if (quote.caseRehearsalSnapshot && !next.quotes[quoteId]) {
                next.quotes[quoteId] = quote;
              }
            }
          }
          currentState.current = next;
          setState(next);
        } catch (error) {
          skipNextPersist.current = false;
          message.error(error instanceof Error ? error.message : '操作失败');
        }
      },
      navigateRoute,
      reset() {
        try {
          const resetState = repository.reset();
          const resetIdentity = acceptanceIdentityFromState(resetState);
          identityPort.save(resetIdentity);
          persistenceContract.current = {
            state: resetState,
            status: 'missing',
            autoPersist: true,
          };
          setState(applyAcceptanceIdentity(resetState, resetIdentity));
          setResetEpoch((current) => current + 1);
          message.success('已重置 CPQ v2 演示数据');
        } catch {
          message.error('本地存储不可用，未能重置演示数据');
        }
      },
      clearCustomerBindingHistory() {
        if (!repository.clearCustomerBindingHistory) {
          message.error('当前运行模式不提供客户绑定历史清理');
          return;
        }
        try {
          const cleared = repository.clearCustomerBindingHistory();
          const clearedIdentity = acceptanceIdentityFromState(cleared);
          identityPort.save(clearedIdentity);
          persistenceContract.current = {
            state: cleared,
            status: 'valid-v5',
            autoPersist: true,
          };
          setState(applyAcceptanceIdentity(cleared, clearedIdentity));
          setResetEpoch((current) => current + 1);
          message.success('已清理客户绑定演示历史，基础目录已保留');
        } catch {
          message.error('本地存储不可用，未能清理客户绑定演示历史');
        }
      },
    }),
    [
      identityPort,
      message,
      repository,
      state,
      transientRoutes,
      resetEpoch,
      workspaceSessionScope,
      ensureFormalWorkspaceRecovery,
      isFormalWorkspaceRecoveryCurrent,
      workspacePreferencesPort,
      unifiedFieldValuePort,
      getCurrentState,
      registerQuoteLeaveGuard,
      navigateRoute,
    ],
  );

  if (routeError) return <Alert type="error" title={routeError} />;
  return (
    <CpqContext.Provider value={value}>
      <Modal
        open={
          recoveryConflictOpen &&
          workspaceRecoveryFailure?.cause instanceof
            WorkspaceRecoveryConflictError
        }
        title="本地工作台草稿发生冲突"
        onCancel={() => setRecoveryConflictOpen(false)}
        footer={
          <Button type="primary" onClick={() => setRecoveryConflictOpen(false)}>
            保留当前内容
          </Button>
        }
      >
        <p>
          另一个页面曾保存这个账号和角色的工作台草稿。为避免覆盖那份内容，本页已暂停保存本地恢复记录；当前报价内容仍保留在本页。
        </p>
        <p>
          如需继续使用本页，可选择“使用本页”，另一页面的本地恢复记录会先备份再替换。若另一页面仍打开，也请核对其未保存内容。完成前请勿刷新或关闭本页。
        </p>
        {import.meta.env.DEV && workspaceRecoveryFailure ? (
          <details>
            <summary>查看诊断</summary>
            {storageFailureSummary(workspaceRecoveryFailure)}
          </details>
        ) : null}
      </Modal>
      {workspaceRecoveryFailure?.cause instanceof
      WorkspaceRecoveryConflictError ? (
        <Alert
          type="warning"
          showIcon
          title="另一页面曾更新本地工作台草稿；本页仍可编辑，请先暂存需要保留的报价再刷新"
          description="本页已暂停保存本地恢复记录，不会覆盖另一页面的内容。"
          action={
            <>
              <Button
                type="primary"
                loading={takingOverRecovery}
                onClick={() => void takeOverFormalWorkspaceRecovery()}
              >
                使用本页
              </Button>
              <Button onClick={() => setRecoveryConflictOpen(true)}>
                查看处理方式
              </Button>
            </>
          }
        />
      ) : null}
      {workspaceRecoveryFailure &&
      !(
        workspaceRecoveryFailure.cause instanceof WorkspaceRecoveryConflictError
      ) ? (
        <Alert
          type="warning"
          showIcon
          title="报价工作台本地恢复点保存失败，刷新或关闭页面后可能无法恢复当前标签"
          description={
            import.meta.env.DEV ? (
              <details>
                <summary>查看诊断</summary>
                {storageFailureSummary(workspaceRecoveryFailure)}
              </details>
            ) : undefined
          }
          action={
            <Button
              onClick={() => void ensureFormalWorkspaceRecovery(true, true)}
            >
              重试保存恢复点
            </Button>
          }
        />
      ) : null}
      {children}
    </CpqContext.Provider>
  );
}

export function useCpq(): CpqContextValue {
  const context = useContext(CpqContext);
  if (!context) throw new Error('useCpq 必须在 CpqProvider 内使用');
  return context;
}

export function useCpqShellController() {
  const {
    state,
    navigateRoute,
    reset,
    bindAcceptanceIdentity,
    workspaceSessionScope,
    ensureFormalWorkspaceRecovery,
    isFormalWorkspaceRecoveryCurrent,
    getCurrentState,
  } = useCpq();
  const identity = useAcceptanceIdentitySelector(state, bindAcceptanceIdentity);
  const pendingInventoryReminders = inventoryReminderAccess(state).canView
    ? state.stockReminders.filter(
        (reminder) =>
          reminder.status === 'OPEN' &&
          hasValidStockReminderBinding(state, reminder),
      ).length
    : 0;
  return {
    activeView: state.activeView,
    hasUnsavedWorkspaceDrafts: Object.values(state.quotes).some(
      (quote) => quote.dirty,
    ),
    ensureFormalWorkspaceRecovery,
    isFormalWorkspaceRecoveryCurrent,
    hasUnsavedFormalWorkspaceDrafts: (user: CurrentUser | undefined) =>
      hasUnsavedFormalWorkspaceDrafts(
        getCurrentState(),
        user,
        workspaceSessionScope,
      ),
    workspacePicker: state.workspacePicker,
    pendingReceiptQuotes: pendingReceiptQuoteCount(state),
    pendingInventoryReminders,
    identity,
    identitySelectionPending:
      identity.roleId !== state.currentRoleId || !identity.userId,
    showDeviceShortcut: canUseActivePermissionAction(
      state,
      'workspace',
      'workspace:quote.read',
    ),
    showPackageShortcut: canUseActivePermissionAction(
      state,
      'workspace',
      'workspace:quote.read',
    ),
    isViewVisible(
      view: AppView,
      capability?: readonly [featureId: string, actionId: string],
    ) {
      if (view === 'permissions') return canUsePermissionCenter(state);
      if (view === 'settings') return canAccessSettings(state);
      return Boolean(
        capability && canUseActivePermissionAction(state, ...capability),
      );
    },
    activateDeviceShortcut() {
      navigateRoute((current) => ({
        activeView: 'workspace',
        activeQuoteId: current.activeQuoteId,
        workspacePicker: 'devices',
        openQuoteIds: current.openQuoteIds,
      }));
    },
    activatePackageShortcut() {
      navigateRoute((current) => ({
        activeView: 'workspace',
        activeQuoteId: current.activeQuoteId,
        workspacePicker: 'packages',
        openQuoteIds: current.openQuoteIds,
      }));
    },
    activateView(view: AppView) {
      return navigateRoute((current) => ({
        activeView: view,
        activeQuoteId: current.activeQuoteId,
        workspacePicker:
          view === 'workspace' ? 'plans' : current.workspacePicker,
        openQuoteIds: current.openQuoteIds,
      }));
    },
    reset,
  };
}
